﻿import {
  Button,
  FormControl,
  InputGroup as ChakraInputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { DateFormControl } from 'BrokerTsLib/src/components'
import { useFormContext } from 'react-hook-form'
import type { VinOnlineVehicleDto } from 'BrokerTsLib/src/dtos'

import { FormLabel } from '@/components/FormLabel'
import { NumberInput } from '@/components/ui/NumberInput'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { FormSection } from '@/containers/forms/FormSection'
import { CarDetails } from '@/containers/steps/vehicle/Step1/forms/VehicleForm/components/CarDetails'
import { VehicleCategorySelection } from '@/containers/steps/vehicle/Step1/forms/VehicleForm/VehicleCategorySelection'
import { VehicleIdentificationForm } from '@/containers/steps/vehicle/Step1/forms/VehicleForm/VehicleIdentificationForm'
import { VehicleModelSelection } from '@/containers/steps/vehicle/Step1/forms/VehicleForm/VehicleModelSelection'
import { useCheckStockVin } from '@/hooks/useWizard'
import type { FormValues } from './types'

type Props = {
  isFromPs: boolean
  psVehicle?: VinOnlineVehicleDto | null
  onDuplicityChange: (isDuplicate: boolean) => void
}

export const VehicleSection = ({ isFromPs, psVehicle, onDuplicityChange }: Props) => {
  const { control, register, getValues } = useFormContext<FormValues>()

  const { mutateAsync: checkStockVin } = useCheckStockVin()
  const { isOpen: isDuplicityOpen, onOpen: openDuplicity, onClose: closeDuplicity } = useDisclosure()

  const checkVinDuplicity = async (vin?: string | null) => {
    const v = (vin ?? '').trim()
    if (!v) { onDuplicityChange(false); return }
    try {
      const { exists } = await checkStockVin(v)
      onDuplicityChange(exists)
      if (exists) openDuplicity()
    } catch {
      onDuplicityChange(false)
    }
  }

  if (isFromPs) {
    return (
      <FormSection number={1} sectionTitle="Vozidlo">
        <FormBody>
          {psVehicle && <CarDetails vehicle={psVehicle} showInsertButton={false} />}
        </FormBody>
      </FormSection>
    )
  }

  return (
    <FormSection number={1} sectionTitle="Vozidlo">
      <FormBody>
        <VehicleIdentificationForm
          showTechCertNumber
          onVinBlur={(vin) => void checkVinDuplicity(vin)}
          onAfterInsertToForm={() => void checkVinDuplicity(getValues('vehicle.vin'))}
        />

        <VehicleCategorySelection />

        <VehicleModelSelection />

        <FormRow>
          <DateFormControl
            name="vehicle.firstRegistrationDate"
            label="Datum první registrace"
            control={control}
            maxDate={new Date()}
            autoComplete={false}
          />

          <FormControl>
            <FormLabel>Zdvihový objem</FormLabel>
            <ChakraInputGroup>
              <NumberInput {...register('vehicle.objem')} />
              <InputRightElement>cm³</InputRightElement>
            </ChakraInputGroup>
          </FormControl>

          <FormControl>
            <FormLabel>Největší technicky přípustná hmotnost</FormLabel>
            <ChakraInputGroup>
              <NumberInput {...register('vehicle.celkovaHmotnost')} />
              <InputRightElement>kg</InputRightElement>
            </ChakraInputGroup>
          </FormControl>

          <FormControl>
            <FormLabel>Max. výkon</FormLabel>
            <ChakraInputGroup>
              <NumberInput {...register('vehicle.vykon1')} />
              <InputRightElement>kW</InputRightElement>
            </ChakraInputGroup>
          </FormControl>
        </FormRow>

        <Modal isOpen={isDuplicityOpen} onClose={closeDuplicity} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Duplicitní skladové pojištění</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Na zadané VIN již existuje aktivní skladové pojištění. Pod tímto VIN nelze založit
                další skladové pojištění.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button onClick={closeDuplicity}>Rozumím</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </FormBody>
    </FormSection>
  )
}
